import type {
  GetTimeOffOccurrence,
  PostTimeOff,
  PostTimeOffDryRun,
  PostTimeOffOverShifts,
  TimeOffFrequency,
  TimeOffGranularity,
} from "@/server-types";
import {
  addMinutes,
  durationBetween,
  minuteOfDay,
} from "@/pages/Dashboard/Calendar/components/hooks/timeOffForm";
import {
  type CompensationChoice,
  type CompensationDraft,
  type CompensationRateBody,
  choiceFromRate,
  draftFromRate,
  emptyCompensationDraft,
  rateForChoice,
} from "../../TimeOffTypes/timeOffCompensation";

/** What the "Ponavljanje" dropdown offers; the rule each maps to is below. */
export type RepeatOption =
  | "NONE"
  | "DAILY"
  | "WEEKLY"
  | "BIWEEKLY"
  | "MONTHLY"
  | "CUSTOM"
  | "DATES";

/** Bit 0 is Monday, as on the server. */
export const ALL_WEEKDAYS = 0b1111111;

/** `MAX_BATCH_TARGETS` and `MAX_ABSENCE_TARGETS` on the server. */
export const MAX_TIME_OFF_TARGETS = 50;

const DEFAULT_DURATION_MINS = 30;

export type TimeOffFormValues = {
  typeId: number | null;
  userIds: number[];
  resourceIds: number[];
  /** Resource rows only: the tile's label, editable. */
  title: string;
  note: string;
  /** The inverse of `blocksOnlineBooking`, which is what the checkbox asks. */
  allowsOnlineBooking: boolean;
  /** An hour-level block's day, and a day-level absence's first day. */
  date: string;
  /** A day-level absence's last day. */
  endDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  /** Part day: the first day's start, "" for the start of the shift. */
  dayStartTime: string;
  /** Part day: the last day's end, "" for the end of the shift. */
  dayEndTime: string;
  repeat: RepeatOption;
  weekdayMask: number;
  /** "Vsakih N tednov". */
  customInterval: number;
  endsOn: string;
  pickedDates: string[];
  compensationChoice: CompensationChoice;
  compensation: CompensationDraft;
};

export type TimeOffRecurrence = PostTimeOff["body"]["recurrence"];

type Detail = GetTimeOffOccurrence["response"];

export function blankTimeOffValues(initial: {
  date: string;
  startTime?: string;
  endTime?: string;
  userId?: number | null;
  resourceId?: number | null;
}): TimeOffFormValues {
  const startTime = initial.startTime ?? "09:00";
  return {
    typeId: null,
    userIds: initial.userId != null ? [initial.userId] : [],
    resourceIds:
      initial.userId == null && initial.resourceId != null
        ? [initial.resourceId]
        : [],
    title: "",
    note: "",
    allowsOnlineBooking: false,
    date: initial.date,
    endDate: initial.date,
    startTime,
    endTime: initial.endTime ?? addMinutes(startTime, DEFAULT_DURATION_MINS),
    allDay: true,
    dayStartTime: "",
    dayEndTime: "",
    repeat: "NONE",
    weekdayMask: weekdayBit(initial.date),
    customInterval: 3,
    endsOn: addDays(initial.date, 30),
    pickedDates: [],
    compensationChoice: "inherit",
    compensation: emptyCompensationDraft(),
  };
}

/** The Monday-first bit for a `YYYY-MM-DD` date. */
export function weekdayBit(date: string): number {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return 1 << ((day + 6) % 7);
}

export function addDays(date: string, days: number): string {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/** Toggles one weekday, but never the last one ticked: a rule needs a day. */
export function toggleWeekday(mask: number, weekdayIndex: number): number {
  const next = mask ^ (1 << weekdayIndex);
  return next === 0 ? mask : next;
}

export function repeatUsesWeekdays(repeat: RepeatOption): boolean {
  return (
    repeat === "DAILY" ||
    repeat === "WEEKLY" ||
    repeat === "BIWEEKLY" ||
    repeat === "CUSTOM"
  );
}

export function repeatUsesEndDate(repeat: RepeatOption): boolean {
  return repeat !== "NONE" && repeat !== "DATES";
}

/** Every day for "Vsak dan"; the date's own weekday for the weekly options. */
export function initialWeekdayMask(repeat: RepeatOption, date: string): number {
  return repeat === "DAILY" ? ALL_WEEKDAYS : weekdayBit(date);
}

/** The mask is only read for `WEEKLY`, so "Vsak dan" with days off is a weekly rule. */
export function toRecurrence(values: TimeOffFormValues): TimeOffRecurrence {
  const { repeat, weekdayMask, endsOn } = values;

  switch (repeat) {
    case "NONE":
      return null;
    case "DAILY":
      return weekdayMask === ALL_WEEKDAYS
        ? { frequency: "DAILY", endsOn }
        : { frequency: "WEEKLY", interval: 1, weekdayMask, endsOn };
    case "WEEKLY":
      return { frequency: "WEEKLY", interval: 1, weekdayMask, endsOn };
    case "BIWEEKLY":
      return { frequency: "WEEKLY", interval: 2, weekdayMask, endsOn };
    case "CUSTOM":
      return {
        frequency: "WEEKLY",
        interval: values.customInterval,
        weekdayMask,
        endsOn,
      };
    case "MONTHLY":
      return { frequency: "MONTHLY", endsOn };
    case "DATES":
      return { dates: [...new Set(values.pickedDates)].sort() };
  }
}

/** The form's repeat fields for a stored rule, the inverse of `toRecurrence`. */
export function repeatFromSeries(
  series: Detail["series"],
  date: string,
): Pick<
  TimeOffFormValues,
  "repeat" | "weekdayMask" | "customInterval" | "endsOn" | "pickedDates"
> {
  const blank = {
    weekdayMask: weekdayBit(date),
    customInterval: 3,
    endsOn: addDays(date, 30),
    pickedDates: [],
  };
  if (series == null) return { ...blank, repeat: "NONE" };

  if (series.isPickedDates) {
    return {
      ...blank,
      repeat: "DATES",
      pickedDates: series.pickedDates ?? [],
    };
  }

  const endsOn = series.endsOn;
  const weekdayMask = series.weekdayMask ?? weekdayBit(series.startsOn);
  const frequency: TimeOffFrequency = series.frequency;

  if (frequency === "DAILY") {
    return { ...blank, repeat: "DAILY", weekdayMask: ALL_WEEKDAYS, endsOn };
  }
  if (frequency === "MONTHLY") {
    return { ...blank, repeat: "MONTHLY", endsOn };
  }
  if (series.interval === 1) {
    return { ...blank, repeat: "WEEKLY", weekdayMask, endsOn };
  }
  if (series.interval === 2) {
    return { ...blank, repeat: "BIWEEKLY", weekdayMask, endsOn };
  }
  return {
    ...blank,
    repeat: "CUSTOM",
    weekdayMask,
    customInterval: series.interval,
    endsOn,
  };
}

export function sameRecurrence(
  a: TimeOffRecurrence,
  b: TimeOffRecurrence,
): boolean {
  return JSON.stringify(normalise(a)) === JSON.stringify(normalise(b));
}

function normalise(recurrence: TimeOffRecurrence) {
  if (recurrence == null) return null;
  if ("dates" in recurrence) return { dates: [...recurrence.dates].sort() };
  return {
    frequency: recurrence.frequency,
    interval: recurrence.interval ?? 1,
    weekdayMask:
      recurrence.frequency === "WEEKLY"
        ? (recurrence.weekdayMask ?? null)
        : null,
    endsOn: recurrence.endsOn,
  };
}

function formatMinuteOfDay(minute: number): string {
  const hours = Math.floor(minute / 60) % 24;
  return `${String(hours).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

export type TimeOffDetailFields = Pick<
  Detail,
  | "date"
  | "timeFrom"
  | "timeTo"
  | "staff"
  | "resource"
  | "note"
  | "series"
  | "absence"
> &
  Partial<Pick<Detail, "compensation">>;

export type HourLevelRetypeFields = Pick<
  Detail,
  "staff" | "type" | "seriesId" | "summary"
>;

/** Only a standalone day row may become hour-level: an edit of a wider absence would drop its other days. */
export function canBecomeHourLevel(detail: HourLevelRetypeFields): boolean {
  return (
    detail.staff != null &&
    detail.type?.granularity === "DAY" &&
    detail.seriesId == null &&
    detail.summary.rowCount === 1
  );
}

/** The form an opened occurrence fills in, hour-level or day-level. */
export function valuesFromDetail(
  detail: TimeOffDetailFields,
): TimeOffFormValues {
  const startTime = detail.timeFrom.slice(11, 16);
  const endTime = detail.timeTo.slice(11, 16);
  const base: TimeOffFormValues = {
    ...blankTimeOffValues({ date: detail.date, startTime, endTime }),
    typeId: detail.staff?.typeId ?? null,
    userIds: detail.staff ? [detail.staff.userId] : [],
    resourceIds: detail.resource ? [detail.resource.resourceId] : [],
    title: detail.resource?.title ?? "",
    note: detail.note ?? "",
    allowsOnlineBooking: detail.staff
      ? !detail.staff.blocksOnlineBooking
      : false,
    ...repeatFromSeries(detail.series, detail.date),
    compensationChoice: choiceFromRate(detail.compensation?.override ?? null),
    compensation: draftFromRate(detail.compensation?.override ?? null),
  };

  const { absence } = detail;
  if (absence == null) return base;

  return {
    ...base,
    userIds: absence.userIds,
    date: absence.startsOn,
    endDate: absence.endsOn,
    allDay: absence.startMinuteOfDay == null && absence.endMinuteOfDay == null,
    dayStartTime:
      absence.startMinuteOfDay == null
        ? ""
        : formatMinuteOfDay(absence.startMinuteOfDay),
    dayEndTime:
      absence.endMinuteOfDay == null
        ? ""
        : formatMinuteOfDay(absence.endMinuteOfDay),
    repeat: "NONE",
  };
}

/** The server starts a changed rule on the opened occurrence, so the form's date follows it until the rule is put back. */
export function pinRuleChangeDate(
  values: TimeOffFormValues,
  occurrenceDate: string | null,
  ruleChanged: boolean,
): TimeOffFormValues {
  return ruleChanged && occurrenceDate != null
    ? { ...values, date: occurrenceDate }
    : values;
}

/** `POST /time-off`'s body for an hour-level block. */
export function toTimeOffBody(
  values: TimeOffFormValues,
  locationId: number,
): PostTimeOff["body"] {
  const hasStaff = values.userIds.length > 0;
  const hasResources = values.resourceIds.length > 0;
  const recurrence = toRecurrence(values);

  return {
    locationId,
    targets: [
      ...values.userIds.map((userId) => ({ kind: "staff" as const, userId })),
      ...values.resourceIds.map((resourceId) => ({
        kind: "resource" as const,
        resourceId,
      })),
    ],
    // A resources-only block carries no type, which the endpoint refuses.
    ...(hasStaff && values.typeId != null ? { typeId: values.typeId } : {}),
    ...(hasResources ? { title: values.title.trim() || null } : {}),
    ...(hasStaff ? { blocksOnlineBooking: !values.allowsOnlineBooking } : {}),
    note: values.note.trim() || null,
    startsOn:
      recurrence != null && "dates" in recurrence
        ? (recurrence.dates[0] ?? values.date)
        : values.date,
    startMinuteOfDay: minuteOfDay(values.startTime),
    durationMins: durationBetween(values.startTime, values.endTime),
    recurrence,
  };
}

/**
 * The `compensation` field of a staff write, or nothing for a caller who may not
 * see staff cost — the server refuses the field from them. Null puts the time
 * off back on its type's rate.
 */
export function compensationField(
  values: TimeOffFormValues,
  canSeeStaffCost: boolean,
): { compensation?: CompensationRateBody | null } {
  if (!canSeeStaffCost || values.userIds.length === 0) return {};
  const rate = rateForChoice(values.compensationChoice, values.compensation);
  return rate === undefined ? {} : { compensation: rate };
}

/** `POST /time-off/over-shifts`'s body without its location. */
export function toAbsenceBody(
  values: TimeOffFormValues,
): Omit<PostTimeOffOverShifts["body"], "locationId"> {
  const partDay = values.allDay
    ? {}
    : {
        ...(values.dayStartTime
          ? { startMinuteOfDay: minuteOfDay(values.dayStartTime) }
          : {}),
        ...(values.dayEndTime
          ? { endMinuteOfDay: endMinuteOf(values.dayEndTime) }
          : {}),
      };

  return {
    userIds: values.userIds,
    typeId: values.typeId as number,
    note: values.note.trim() || null,
    startsOn: values.date,
    endsOn: values.endDate,
    ...partDay,
  };
}

/** The form fields that stop a save, keyed as `useForm` reports them. */
export type TimeOffFieldError =
  | "NO_TARGETS"
  | "TOO_MANY_TARGETS"
  | "NO_TYPE"
  | "INVALID_TIME"
  | "INVALID_DATE"
  | "END_BEFORE_START"
  | "NO_WEEKDAY"
  | "NO_DATES"
  | "REPEAT_ENDS_BEFORE_START"
  | "INVALID_COMPENSATION";

export function isTimeOfDay(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** A real `YYYY-MM-DD` day, so 2026-02-30 is refused rather than rolled into March. */
export function isLocalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

type TimeOffPreview = PostTimeOffDryRun["response"];

/**
 * Consumers read `timePreview.targets` as an array; a non-conforming dry-run
 * response (e.g. a proxy 200) would otherwise crash the form, so treat it as
 * no preview.
 */
export function isTimeOffPreview(
  data: TimeOffPreview | undefined,
): data is TimeOffPreview {
  return data != null && Array.isArray(data.targets);
}

/** An end at 00:00 is the day's own midnight, the end of that day. */
function endMinuteOf(time: string): number {
  return minuteOfDay(time) || 24 * 60;
}

/** Every refusal the form can tell before the server does. */
export function findFieldErrors(
  values: TimeOffFormValues,
  granularity: TimeOffGranularity,
): Partial<Record<keyof TimeOffFormValues, TimeOffFieldError>> {
  const errors: Partial<Record<keyof TimeOffFormValues, TimeOffFieldError>> =
    {};
  const targets = values.userIds.length + values.resourceIds.length;

  if (targets === 0) errors.userIds = "NO_TARGETS";
  else if (targets > MAX_TIME_OFF_TARGETS) errors.userIds = "TOO_MANY_TARGETS";

  if (values.userIds.length > 0 && values.typeId == null) {
    errors.typeId = "NO_TYPE";
  }
  if (
    rateForChoice(values.compensationChoice, values.compensation) === undefined
  ) {
    errors.compensation = "INVALID_COMPENSATION";
  }

  if (granularity === "DAY") {
    const hasStart = isLocalDate(values.date);
    if (!hasStart) errors.date = "INVALID_DATE";
    if (!isLocalDate(values.endDate)) errors.endDate = "INVALID_DATE";
    else if (hasStart && values.endDate < values.date) {
      errors.endDate = "END_BEFORE_START";
    }
    if (values.allDay) return errors;

    const startValid =
      values.dayStartTime !== "" && isTimeOfDay(values.dayStartTime);
    const endValid = values.dayEndTime !== "" && isTimeOfDay(values.dayEndTime);
    if (values.dayStartTime !== "" && !startValid) {
      errors.dayStartTime = "INVALID_TIME";
    }
    if (values.dayEndTime !== "" && !endValid) {
      errors.dayEndTime = "INVALID_TIME";
    } else if (
      startValid &&
      endValid &&
      values.date === values.endDate &&
      endMinuteOf(values.dayEndTime) - minuteOfDay(values.dayStartTime) <= 0
    ) {
      errors.dayEndTime = "END_BEFORE_START";
    }
    return errors;
  }

  const startValid = isTimeOfDay(values.startTime);
  if (!startValid) errors.startTime = "INVALID_TIME";
  if (!isTimeOfDay(values.endTime)) errors.endTime = "INVALID_TIME";
  else if (
    startValid &&
    durationBetween(values.startTime, values.endTime) <= 0
  ) {
    errors.endTime = "END_BEFORE_START";
  }

  const hasDate = values.repeat === "DATES" || isLocalDate(values.date);
  if (!hasDate) errors.date = "INVALID_DATE";
  if (repeatUsesWeekdays(values.repeat) && values.weekdayMask === 0) {
    errors.weekdayMask = "NO_WEEKDAY";
  }
  if (values.repeat === "DATES" && values.pickedDates.length === 0) {
    errors.pickedDates = "NO_DATES";
  }
  if (repeatUsesEndDate(values.repeat)) {
    if (!isLocalDate(values.endsOn)) errors.endsOn = "INVALID_DATE";
    else if (hasDate && values.endsOn < values.date) {
      errors.endsOn = "REPEAT_ENDS_BEFORE_START";
    }
  }
  return errors;
}
